import { AxiosResponse } from "axios";
import {
  httpClient,
  httpClientNobaseUrl,
  uploadImageAndGetCdnUrl,
} from "../../../service/http";
import {
  CreateProductPayload,
  GetProductListActionFilters,
  Product,
  ProductListItem,
  UpdateProductPayload,
} from "../models/product";
import { OrganizationKeyName } from "../../../model/Organization";
import { UploadImage } from "../../../service/api-response/UploadImage";
import {
  DeletePayload,
  PostPayload,
  PutPayload,
} from "../../../model/http-crud-models";
import { PaginatedResult } from "../../../model/paginated-result";

export const productsHttp = {
  createProduct: async ({
    organizationKeyName,
    data,
  }: PostPayload<CreateProductPayload>): Promise<string> => {
    const res = await httpClient<{ id: string }>({
      method: "POST",
      url: `/organizations/${organizationKeyName}/shop-products`,
      data,
    });
    return res.id;
  },

  getShopProductList: async (
    organizationKeyName: OrganizationKeyName,
    filters: GetProductListActionFilters
  ) => {
    const {
      shopId,
      tags,
      productCategory,
      kitchenCategory,
      currencies,
      countryAlpha2Codes,
      ...restFilters
    } = filters;
    const params = new URLSearchParams();
    Object.entries(restFilters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });
    if (tags) {
      tags.forEach((tag) => {
        params.append("tags[]", tag);
      });
    }
    if (productCategory) {
      productCategory.forEach((category) => {
        params.append("productCategories[]", category);
      });
    }
    if (kitchenCategory) {
      kitchenCategory.forEach((category) => {
        params.append("kitchenCategories[]", category);
      });
    }

    if (currencies) {
      currencies.forEach((currency) => {
        params.append("currencies[]", currency);
      });
    }

    if (countryAlpha2Codes) {
      countryAlpha2Codes.forEach((country) => {
        params.append("countryAlpha2Codes[]", country);
      });
    }

    const url = `/organizations/${organizationKeyName}/shops/${shopId}/products?${params.toString()}`;

    const res = await httpClient<PaginatedResult<ProductListItem[]>>({
      method: "GET",
      url,
    });
    return res.data;
  },

  getProductList_: async ({
    url,
    filters,
  }: {
    url: string;
    filters: GetProductListActionFilters;
  }) => {
    const {
      shopId,
      tags,
      productCategory,
      kitchenCategory,
      currencies,
      countryAlpha2Codes,
      ...restFilters
    } = filters;
    const params = new URLSearchParams();
    const url_ = new URL(url);
    const searchParams = new URLSearchParams(url_.search);
    searchParams.forEach((value, key) => {
      params.set(key, value);
    });
    Object.entries(restFilters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });
    if (tags) {
      tags.forEach((tag) => {
        params.append("tags[]", tag);
      });
    }
    if (productCategory) {
      productCategory.forEach((category) => {
        params.append("productCategories[]", category);
      });
    }
    if (kitchenCategory) {
      kitchenCategory.forEach((category) => {
        params.append("kitchenCategories[]", category);
      });
    }

    if (currencies) {
      currencies.forEach((currency) => {
        params.append("currencies[]", currency);
      });
    }

    if (countryAlpha2Codes) {
      countryAlpha2Codes.forEach((country) => {
        params.append("countryAlpha2Codes[]", country);
      });
    }

    return httpClientNobaseUrl<PaginatedResult<ProductListItem[]>>({
      method: "GET",
      url: `${url_.origin}${url_.pathname}?${params.toString()}`,
    });
  },

  getProduct: async (organizationKeyName: OrganizationKeyName, id?: string) => {
    if (!id) return;
    const res = await httpClient<{ data: Product }>({
      method: "GET",
      url: `/organizations/${organizationKeyName}/shop-products/${id}`,
    });
    return res.data;
  },

  updateProduct: ({
    data: { id, ...rest },
    organizationKeyName,
  }: PutPayload<
    UpdateProductPayload & { id: string }
  >): Promise<AxiosResponse> => {
    return httpClient({
      method: "PUT",
      url: `/organizations/${organizationKeyName}/shop-products/${id}`,
      data: rest,
    });
  },

  archiveProduct: async ({
    data: { id },
    organizationKeyName,
  }: DeletePayload) => {
    if (!id) return;

    return httpClient<{}>({
      method: "POST",
      url: `/organizations/${organizationKeyName}/shop-products/${id}/archive`,
      data: {},
    });
  },

  uploadProductImage: async ({
    file,
    id,
    organizationKeyName,
  }: UploadImage) => {
    return uploadImageAndGetCdnUrl({
      file,
      id,
      organizationKeyName,
      apiRoute: "shop-product",
    });
  },
};
